/* eslint-disable @typescript-eslint/no-explicit-any */
// Styling
import {
	CategoryScale,
	Chart as ChartJS,
	ChartOptions,
	Filler,
	Legend,
	LineElement,
	LinearScale,
	PointElement,
	ScriptableContext,
	Title,
	Tooltip,
} from 'chart.js';
import _ from 'lodash';
import moment from 'moment';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { ITimeMachine, Skeleton, tokens } from '@treams/treams-ui';

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	Filler,
);

const LineChartBackground = styled(Box)`
	position: relative;

	::after {
		content: '';
		display: block;
		position: absolute;
		left: 20px;
		top: 10px;
		bottom: 20px;
		right: 20px;
		background: ${tokens.surface.primary};
	}
`;

export function LineChartGradient(props: {
	labels: string[];
	data: number[] | { x: number; y: number }[];
	height?: number;
	bounds?: [number, number];
	autoBound?: boolean;
	compactLayout?: boolean;
	minimalLayout?: boolean;
	showBackground?: boolean;
	yFormat?: (x: number) => string;
	tintColor?: string;
}) {
	let [min, max] = props.bounds ?? [undefined, undefined];
	if (props.autoBound && props.data.length > 0) {
		const ys =
			typeof props.data[0] === 'number'
				? (props.data as number[])
				: (props.data as { y: number }[]).map((p) => p.y);
		const [absMin, absMax] = [_.min(ys) ?? 0, _.max(ys) ?? 0];
		const minMargin = 1e-3;
		const rangeOrder = Math.round(Math.log10(absMax - absMin + minMargin * 2));
		const maxOffset = Math.pow(10, rangeOrder);

		min = Math.floor((absMin - minMargin) / maxOffset) * maxOffset;
		max = Math.ceil((absMax + minMargin) / maxOffset) * maxOffset;

		if (props.bounds) {
			min = Math.max(min, props.bounds[0]);
			max = Math.min(max, props.bounds[1]);
		}
	}

	const hexColorGradient = (context: CanvasRenderingContext2D, color: string) => {
		const [red, green, blue] = [
			color.substring(1, 3),
			color.substring(3, 5),
			color.substring(5, 7),
		].map((s) => parseInt(s, 16));

		const gradient = context.createLinearGradient(0, 0, 0, props.height ?? 200);
		gradient.addColorStop(0, `rgba(${red}, ${green}, ${blue}, 0.4)`);
		gradient.addColorStop(1, `rgba(${red}, ${green}, ${blue}, 0.0)`);

		return gradient;
	};

	const data = () => {
		return {
			labels: props.labels,
			datasets: [
				{
					data: props.data,
					fill: 'start',
					backgroundColor: (context: ScriptableContext<'line'>) =>
						hexColorGradient(context.chart.ctx, props.tintColor ?? tokens.info.icon),
					borderColor: props.tintColor ?? tokens.info.icon,
					pointBackgroundColor: 'transparent',
					pointBorderColor: 'transparent',
					pointRadius: 20,
					pointHoverRadius: 20,
					pointStyle: false,
				},
			],
		};
	};

	const options: ChartOptions<'line'> = {
		maintainAspectRatio: false,
		responsive: true,
		layout: {
			padding: 0,
		},
		scales: {
			x: {
				display: !(props.compactLayout || props.minimalLayout),
				grid: {
					display: false,
				},
				ticks: {
					display: !props.compactLayout,
					color: tokens.text.secondary,
					font: {
						weight: 'bold',
						size: 12,
						family: 'Source Sans Pro',
					},
				},
			},
			y: {
				display: !props.minimalLayout,
				beginAtZero: true,
				min,
				max,
				grid: {
					display: true,
					color: tokens.border.subtle,
				},
				ticks: {
					precision: 0,
					mirror: !!props.compactLayout,
					callback: props.yFormat,
				},
			},
		},
		elements: {
			line: {
				tension: 0.35,
			},
		},
		plugins: {
			filler: {
				propagate: false,
			},
			legend: {
				display: false,
			},
			tooltip: {
				enabled: true,
				callbacks: {
					label: function (context: any) {
						const source = context.dataset.data[context.dataIndex];

						if (source?.tooltip) {
							return source?.tooltip;
						}

						return props.yFormat ? props.yFormat(context.parsed.y) : context.parsed.y.toFixed(1);
					},
				},
			},
		},
		interaction: {
			intersect: true,
		},
	};

	const lineContents = (
		<div style={{ position: 'relative', zIndex: 2 }}>
			<Line data={data() as any} options={options} height={props.height} />
		</div>
	);

	return (
		<div style={{ margin: '-10px -20px -20px -20px' }}>
			{props.showBackground ? (
				<LineChartBackground>{lineContents}</LineChartBackground>
			) : (
				lineContents
			)}
		</div>
	);
}

const AnalyticsGraphMetricEmpty = styled(Flex)`
	height: 120px;
	background-color: ${tokens.surface.primary};
	color: ${tokens.text.secondary};
	align-items: center;
	justify-content: center;
`;

export function AnalyticsGraphMetric(props: {
	metricGroupID: string;
	metric: graphql.AnalyticsGraphMetric;
	timeMachine: ITimeMachine;
}) {
	const { t } = useTranslation();

	const { datapoints } = props.metric;

	if (datapoints.length < 2 || datapoints.every((datapoint) => datapoint.value === 0)) {
		return (
			<AnalyticsGraphMetricEmpty>{t('analytics.metrics.graph.empty')}</AnalyticsGraphMetricEmpty>
		);
	}

	const boundedDatapoints = datapoints.map((datapoint, index) => {
		// Force to use the lower bound of the time machine.
		if (index === 0) {
			return {
				...datapoint,
				timestamp: moment(props.timeMachine.current[0]).toDate(),
			};
		}

		// Force to use the upper bound of the time machine.
		if (index === datapoints.length - 1) {
			return {
				...datapoint,
				timestamp: moment(props.timeMachine.current[1]).toDate(),
			};
		}

		return datapoint;
	});

	return (
		<LineChartGradient
			data={boundedDatapoints.map((datapoint) => datapoint.value) as any}
			labels={boundedDatapoints.map((datapoint) => moment(datapoint.timestamp).format('LL'))}
			bounds={
				typeof props.metric.min === 'number' && typeof props.metric.max === 'number'
					? [props.metric.min, props.metric.max]
					: undefined
			}
			compactLayout
			showBackground
		/>
	);
}

AnalyticsGraphMetric.Skeleton = styled(Skeleton.Box)`
	height: 120px;
	width: 100%;
`;
